import React from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { useSubmitMessages } from 'utils'
import {
  ADD_TECH_COMMENTS_ACTION,
  ADD_TECH_COMMENTS_LABEL,
  STALE_WORK_ORDER_MESSAGE,
  TECH_COMMENTS_SAVED_MESSAGE,
} from '../constants'
import { isStaleWorkOrderError } from '../specialActions'
import AddTechCommentsFields, {
  techCommentsValidation,
} from './AddTechCommentsFields'

// Standalone special actions partial for work orders that allow tech comments
// but have no other manager partial (e.g. Tech On Site). The only action
// offered is Add Tech Comments; "None" collapses the form again.
const SPECIAL_ACTION_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: ADD_TECH_COMMENTS_ACTION, label: ADD_TECH_COMMENTS_LABEL },
]

function getInitialValues() {
  return {
    special_action: 'none',
    tech_comments: '',
  }
}

const validationSchema = Yup.object({
  tech_comments: techCommentsValidation('special_action'),
})

function AddTechCommentsForm({ onAddTechComments, onRefreshWorkOrder }) {
  const { displaySubmitSuccess, displaySubmitFailure } = useSubmitMessages()

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={async (values, { resetForm }) => {
        if (values.special_action !== ADD_TECH_COMMENTS_ACTION) return
        const resetToInitial = () => resetForm({ values: getInitialValues() })

        try {
          await onAddTechComments({ tech_comments: values.tech_comments })
          displaySubmitSuccess(TECH_COMMENTS_SAVED_MESSAGE)
          resetToInitial()
        } catch (error) {
          if (isStaleWorkOrderError(error)) {
            displaySubmitFailure({
              customFormWideError: STALE_WORK_ORDER_MESSAGE,
            })
            resetToInitial()
            onRefreshWorkOrder()
            return
          }
          displaySubmitFailure(error?.errors, null, error)
        }
      }}
      validateOnBlur={false}
    >
      {({
        values,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        isSubmitting,
      }) => (
        <Form className="accept-decline-section">
          <h3 className="accept-decline-section__title">Special Actions</h3>
          <div className="accept-decline-section__field">
            <label
              className="accept-decline-section__label"
              htmlFor="add-tech-comments-special-actions"
            >
              Action
            </label>
            <select
              id="add-tech-comments-special-actions"
              name="special_action"
              className="accept-decline-section__input-native"
              value={values.special_action}
              onChange={(event) => {
                const nextValue = event.target.value
                setFieldValue('special_action', nextValue)
                if (nextValue !== ADD_TECH_COMMENTS_ACTION) {
                  setFieldValue('tech_comments', '')
                  setFieldTouched('tech_comments', false)
                }
              }}
            >
              {SPECIAL_ACTION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {values.special_action === ADD_TECH_COMMENTS_ACTION && (
            <AddTechCommentsFields
              idPrefix="add-tech-comments"
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
              setFieldTouched={setFieldTouched}
              isSubmitting={isSubmitting}
            />
          )}
        </Form>
      )}
    </Formik>
  )
}

AddTechCommentsForm.propTypes = {
  onAddTechComments: PropTypes.func.isRequired,
  onRefreshWorkOrder: PropTypes.func.isRequired,
}

export default AddTechCommentsForm
